import { arrayToEntityDict, type Entity, type EntityDict } from '@webapp2/shared/model'
import { capitalize } from 'vue'

let db: IDBDatabase | null = null
const dbName = 'OssoVR'
const adminStore = 'admin'
const organizationStore = 'organization'
const version = 5

const init = new Promise<void>(resolve => {
  const request = indexedDB.open(dbName, version)

  request.onsuccess = () => {
    db = request.result
    resolve()
  }
  request.onerror = () => {
    console.error('Cache init failure', request.error)
    resolve()
  }

  request.onupgradeneeded = event => {
    const database = (event.target as IDBOpenDBRequest).result
    for (const name of Array.from(database.objectStoreNames)) {
      database.deleteObjectStore(name)
    }
    const orgStore = database.createObjectStore(organizationStore)
    orgStore.createIndex('type', 'type', { unique: false })
    const admStore = database.createObjectStore(adminStore)
    admStore.createIndex('type', 'type', { unique: false })
  }
})

function getStore(name: string, mode: 'readonly' | 'readwrite'): IDBObjectStore {
  const transaction = db!.transaction([name], mode)
  return transaction.objectStore(name)
}

export class Cache {
  constructor(private name: string) {}

  async get<T>(id: string): Promise<T | undefined> {
    await init
    const store = getStore(this.name, 'readonly')
    return new Promise(resolve => {
      const request = store.get(id) as IDBRequest<T>
      request.onsuccess = () => resolve(request.result || undefined)
      request.onerror = () => {
        console.error(`Cache get failure ${id}`, request.error)
        resolve(undefined)
      }
    })
  }

  async set<T>(id: string, value: T): Promise<void> {
    await init
    const store = getStore(this.name, 'readwrite')

    return new Promise(resolve => {
      const request = store.put(value, id)
      request.onsuccess = () => resolve()
      request.onerror = () => {
        console.error(`Cache set failure ${id}`, request.error)
        resolve()
      }
    })
  }

  async setItems(items: { id: string }[]): Promise<void> {
    if (items.length === 0) {
      return
    }

    await init
    return new Promise(resolve => {
      const transaction = db!.transaction([this.name], 'readwrite')
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => {
        console.error(`Cache setMany failure`, transaction.error)
        resolve()
      }
      const store = transaction.objectStore(this.name)
      items.forEach(item => store.put(item, item.id))
      transaction.commit()
    })
  }

  async deleteByType(type: string): Promise<void> {
    await init

    return new Promise(resolve => {
      const transaction = db!.transaction([this.name], 'readwrite')
      const store = transaction.objectStore(this.name)
      const index = store.index('type')
      const range = IDBKeyRange.only(capitalize(type))
      const cursorRequest = index.openCursor(range)
      transaction.oncomplete = () => resolve()
      cursorRequest.onerror = transaction.onerror = () => {
        console.error(`Cache deleteByType failure`, transaction.error)
        resolve()
      }
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result
        if (cursor) {
          cursor.delete()
          cursor.continue()
        }
      }
    })
  }

  async getByType<T extends Entity>(type: string): Promise<EntityDict<T>> {
    await init
    const store = getStore(this.name, 'readonly')

    return new Promise(resolve => {
      const index = store.index('type')
      const request = index.getAll(capitalize(type))
      request.onsuccess = () => {
        resolve(arrayToEntityDict(request.result as T[]))
        request.onerror = () => {
          console.error(`Cache getByType failure ${type}`, request.error)
          resolve({})
        }
      }
    })
  }

  async clear(): Promise<void> {
    await init
    const store = getStore(this.name, 'readwrite')
    return new Promise(resolve => {
      const request = store.clear()
      request.onsuccess = () => resolve()
      request.onerror = () => {
        console.error(`Cache clear failure`, request.error)
        resolve()
      }
    })
  }
}

export const cache = new Cache(organizationStore)
export const adminCache = new Cache(adminStore)
